/**
 * Telemetry for the «Страница не открылась» screen (`RouteError`, the router
 * `errorElement` in routes/not-found.tsx).
 *
 * ⚠️ Until 2026-10-22 that screen reported NOWHERE. An error a React/router
 * boundary catches is not "unhandled": it never reaches `window.onerror`, so
 * PostHog's `capture_unhandled_errors` sees nothing and the DIY `events` table
 * writes nothing — a whole route can collapse into the error screen for real
 * readers while every dashboard stays clean. On the sister site that is exactly
 * what happened after an onboarding action (owner, 22.10).
 *
 * The suspected cause is Chrome's automatic page translation: Google Translate
 * rewraps text nodes in `<font>` elements behind React's back, and React then
 * throws `NotFoundError` from `removeChild`/`insertBefore` on its next update
 * (facebook/react#11538). Hence the `translated` flag on every report — it is
 * what tells that cause apart from a bug of ours.
 */
import { track } from './funnel';
import { phCaptureException } from './posthog';

/** Longest `meta.message` a `page_error` row carries. */
export const PAGE_ERROR_MESSAGE_MAX = 200;

/**
 * Whether the browser has machine-translated this page, read from the DOM.
 *
 * Three markers, any one is enough:
 *  - `html.translated-ltr` / `html.translated-rtl` — the classes Chrome's
 *    translator puts on `<html>` while a translation is shown;
 *  - `<font style="vertical-align: inherit">` — the wrapper it puts around every
 *    text node it rewrote (the very nodes React then fails to find);
 *  - `<html lang>` naming another language than the site's — the translator
 *    rewrites it to the target language.
 *
 * Only the first two letters are compared (`ru-RU` is `ru`), and an empty `lang`
 * proves nothing. The `lang` signal is the weakest of the three: a crash in the
 * same render as a switch between /ru and /uz could catch `<html lang>` before
 * the provider updates it. Read it together with `message`, never alone.
 *
 * Pure apart from reading `doc`, and never throws — it runs inside an error
 * boundary, where a second exception would replace the error screen itself.
 */
export function isPageTranslated(doc: Document | null | undefined, siteLang: string): boolean {
  if (!doc) return false;
  try {
    const html = doc.documentElement;
    if (!html) return false;
    if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl')) {
      return true;
    }
    const pageLang = (html.getAttribute('lang') ?? '').trim().slice(0, 2).toLowerCase();
    const site = siteLang.trim().slice(0, 2).toLowerCase();
    if (pageLang && site && pageLang !== site) return true;
    return doc.querySelector('font[style*="vertical-align"]') !== null;
  } catch {
    return false;
  }
}

/**
 * Path segments that ARE a credential, replaced by a placeholder before the path
 * leaves the browser: the bot's magic-link code (`/auth/tg/:code`), an invite
 * (`/join/:token`) and the one-shot pay link (`/pay/:token`, "the token in the
 * path is the whole credential" — app.tsx). An error report is kept for 90 days
 * and read by people who must never be able to replay one.
 */
const SECRET_PATH = /^((?:\/(?:ru|uz|kk))?\/(?:auth\/tg|join|pay))\/[^/]+/;

/** The pathname as it may be stored: credential segments masked. */
export function redactRoute(pathname: string): string {
  return pathname.replace(SECRET_PATH, '$1/:token');
}

/** `message`/`name` of whatever was thrown — an Error, a DOMException, or not. */
function describeThrown(error: unknown): { message: string; name: string } {
  if (error !== null && typeof error === 'object') {
    const { message, name, status, statusText } = error as Record<string, unknown>;
    if (typeof message === 'string') {
      return { message, name: typeof name === 'string' ? name : 'Error' };
    }
    // A router error response (`{ status, statusText, data }`).
    if (typeof status === 'number') {
      return {
        message: `${status} ${typeof statusText === 'string' ? statusText : ''}`.trim(),
        name: 'ErrorResponse',
      };
    }
  }
  let message = '';
  try {
    message = String(error);
  } catch {
    // an object with no usable toString — the name still says what it was
  }
  return { message, name: typeof error };
}

/**
 * Report one crash caught by the app boundary: the exception itself to PostHog
 * (`$exception`, so it lands in Error Tracking with its stack) and a `page_error`
 * row to the first-party funnel, which `track()` mirrors to PostHog as well.
 *
 * Each half is wrapped on its own: reporting must never throw inside the
 * boundary, and one failing must not cost the other. SSR/prerender-safe —
 * both callees no-op without a browser.
 */
export function reportPageError(error: unknown, pathname: string, translated: boolean): void {
  const route = redactRoute(pathname);
  try {
    phCaptureException(error, { route, boundary: 'app', translated });
  } catch {
    // never let reporting break the error screen
  }
  try {
    const { message, name } = describeThrown(error);
    track('page_error', {
      route,
      message: message.slice(0, PAGE_ERROR_MESSAGE_MAX),
      name,
      translated,
    });
  } catch {
    // never let reporting break the error screen
  }
}
